import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const RULE_LABEL = { add_vat: "Add VAT on top", reverse_charge: "Reverse charge (no VAT added)", none: "No VAT" };

const Approve = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("affiliate");
  const go = () => axios.post(`${API}/api/affiliate-tax/admin/approve`, { email, role }, auth())
    .then(() => { toast.success(`${email} approved as ${role}`); setEmail(""); onDone(); })
    .catch((e) => toast.error(e.response?.data?.detail === "user_not_found" ? "No account with that email." : "Couldn't approve."));
  return (
    <div className="mt-6 flex flex-wrap gap-2" data-testid="aff-admin-approve">
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="their account email" className="flex-1 min-w-[220px] rounded-lg border border-[#e5e0d3] px-3 py-2" data-testid="aff-admin-email" />
      <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-lg border border-[#e5e0d3] px-3 py-2" data-testid="aff-admin-role">
        <option value="affiliate">Affiliate</option><option value="partner">Partner</option>
      </select>
      <button type="button" onClick={go} className="rounded-full bg-[#2b2b2b] px-5 py-2 text-white hover:bg-black transition-colors" data-testid="aff-admin-approve-btn">Approve</button>
    </div>
  );
};

const Row = ({ a, onDone }) => {
  const t = a.tax;
  const flip = () => axios.post(`${API}/api/affiliate-tax/admin/approve`, { email: a.email, role: a.role, status: a.status === "approved" ? "revoked" : "approved" }, auth())
    .then(onDone).catch(() => toast.error("Couldn't update."));
  return (
    <tr className="border-t border-[#eee9dc]" data-testid={`aff-admin-row-${a.user_id}`}>
      <td className="py-2 pr-3">{a.email}<div className="text-xs text-[#8a8a8a]">{a.role} · {a.status}</div></td>
      <td className="py-2 px-2">{t ? `${t.legal_name} (${t.entity_type})` : <span className="text-[#a0522d]">Not filled in</span>}</td>
      <td className="py-2 px-2">{t?.country || "–"}</td>
      <td className="py-2 px-2">{t ? (t.vat_registered ? t.vat_number : "Not registered") : "–"}</td>
      <td className="py-2 px-2">{t ? (t.account_last4 ? `${t.sort_code} ••${t.account_last4}` : `IBAN ••${t.iban_last4}`) : "–"}</td>
      <td className="py-2 px-2">{t ? `Yes · ${t.consent_at?.slice(0, 10)}` : "–"}</td>
      <td className="py-2 pl-2"><button type="button" onClick={flip} className="text-xs underline hover:text-black" data-testid={`aff-admin-toggle-${a.user_id}`}>{a.status === "approved" ? "Revoke" : "Re-approve"}</button></td>
    </tr>
  );
};

const Rules = ({ rules }) => (
  <div className="mt-8" data-testid="aff-admin-rules">
    <h2 className="text-lg">VAT rules for VAT-registered affiliates</h2>
    <p className="text-xs text-[#6b6b6b]">To be confirmed by your accountant. EU countries: reverse charge. Everything else: the "*" default.</p>
    <div className="mt-2 flex flex-wrap gap-2 text-xs">
      {Object.entries(rules).filter(([c]) => c === "GB" || c === "*" || c === "IE").map(([c, r]) => (
        <span key={c} className="rounded-full border border-[#e5e0d3] bg-white px-3 py-1" data-testid={`aff-admin-rule-${c}`}>{c}: {RULE_LABEL[r.registered]}{r.rate ? ` ${Math.round(r.rate * 100)}%` : ""}</span>
      ))}
    </div>
  </div>
);

export default function AdminAffiliateTax() {
  const [d, setD] = useState(null);
  const [rules, setRules] = useState({});
  const load = useCallback(() => {
    axios.get(`${API}/api/affiliate-tax/admin/list`, auth()).then((r) => setD(r.data.affiliates)).catch(() => setD([]));
    axios.get(`${API}/api/affiliate-tax/admin/rules`, auth()).then((r) => setRules(r.data.rules)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  return (
    <div className="min-h-screen bg-[#fbfaf6] px-6 py-10 text-sm text-[#2b2b2b]" data-testid="aff-admin-page">
      <h1 className="text-3xl" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Affiliates & Partners</h1>
      <p className="mt-1 text-[#6b6b6b]">Approve someone, then send them to /soulumo/affiliate-details to add their tax and bank details.</p>
      <Approve onDone={load} />
      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[760px] text-left">
          <thead className="text-xs uppercase tracking-wider text-[#6b6b6b]"><tr><th className="py-2">Account</th><th>Legal name</th><th>Country</th><th>VAT</th><th>Bank</th><th>Self-billing</th><th></th></tr></thead>
          <tbody>{(d || []).map((a) => <Row key={a.user_id} a={a} onDone={load} />)}</tbody>
        </table>
        {d && d.length === 0 && <p className="mt-3 text-[#8a8a8a]" data-testid="aff-admin-empty">No affiliates yet.</p>}
      </div>
      <Rules rules={rules} />
    </div>
  );
}
